import React, { useEffect, useState } from 'react';
import { Flame, TrendingUp, AlertCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { getUserChildren, getQuizResults, getStreak } from '../lib/supabase';
import { User, QuizResult } from '../types';

interface ChildData {
  user: User;
  streak: number;
  recentResults: QuizResult[];
}

export function ParentDashboard() {
  const { user } = useAuth();
  const [children, setChildren] = useState<ChildData[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadChildren() {
      if (!user) return;

      try {
        const childUsers = await getUserChildren(user.id);

        const childData = await Promise.all(
          childUsers.map(async (child) => {
            const [streak, recentResults] = await Promise.all([
              getStreak(child.id),
              getQuizResults(child.id, undefined, 5),
            ]);

            return {
              user: child,
              streak,
              recentResults,
            };
          })
        );

        setChildren(childData);
      } catch (error) {
        console.error('Error loading children:', error);
      } finally {
        setIsLoading(false);
      }
    }

    loadChildren();
  }, [user]);

  if (isLoading) {
    return (
      <div className="loading">
        <p>Laden...</p>
      </div>
    );
  }

  return (
    <div className="parent-dashboard">
      <div className="dashboard-greeting">
        <h1>Eltern-Übersicht</h1>
        <p>Sehe den Lernfortschritt deiner Kinder</p>
      </div>

      {children.length === 0 ? (
        <div className="empty-state">
          <AlertCircle size={48} />
          <p>Keine verknüpften Schüler gefunden.</p>
          <p className="hint">
            Schüler können in ihrem Profil einen Eltern-Account verknüpfen.
          </p>
        </div>
      ) : (
        <div className="children-list">
          {children.map((child) => (
            <div key={child.user.id} className="child-card">
              <div className="child-header">
                <h2>{child.user.name}</h2>
                <div className="child-streak">
                  <Flame size={18} />
                  <span>{child.streak} Tage</span>
                </div>
              </div>

              <div className="child-stats">
                <h3>Letzte Quiz-Ergebnisse</h3>
                {child.recentResults.length > 0 ? (
                  <div className="results-list">
                    {child.recentResults.map((result) => (
                      <div key={result.id} className="result-item">
                        <span className="result-date">
                          {new Date(result.created_at).toLocaleDateString('de-DE')}
                        </span>
                        <span className="result-course">{result.course_id}</span>
                        <span
                          className={`result-score ${
                            result.percentage >= 70 ? 'good' : 'needs-work'
                          }`}
                        >
                          {result.percentage}%
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-results">Noch keine Quiz-Ergebnisse</p>
                )}
              </div>

              <div className="child-actions">
                <TrendingUp size={16} />
                <span>Details ansehen</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
